import React, { useState } from 'react';
import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  FormControl, 
  OutlinedInput, 
  InputAdornment, 
  FormHelperText, 
  FormGroup, 
  FormControlLabel, 
  Switch,
  Typography,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  root: {
    width: '100%',
  },
  heading: {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  accordionDetails: {
    display: 'flex',           // added this line
    flexDirection: 'column',
    gap: '10px',
  }
});

export default function DrawerContent({ drawing, setDrawing }) {
  const classes = useStyles();
  const [expandedPanel, setExpandedPanel] = useState({
    machineSetup: true,
    sprayerSetup: false,
    plowSetup: false,
    irrigationSetup: false,
    systemSetup: false
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel({
      ...expandedPanel,
      [panel]: isExpanded
    });
  };

  const [inputValue, setInputValue] = useState(drawing.bedWidth/10);

  const handleBedWidthChange = (e) => {
    const value = e.target.value;
    
    // Only update input value, not the state
    setInputValue(value);

    if (!isNaN(value) && value >= 20) {
      setDrawing({ ...drawing, bedWidth: value * 10 });
    }
  };

  const handleBedWidthBlur = () => {
    // When the input loses focus, validate and update state
    const numValue = parseInt(inputValue);
    
    if (!isNaN(numValue) && numValue >= 20) {
      setDrawing({ ...drawing, bedWidth: numValue * 10 });
    } else {
      // Reset to previous valid value or clear input
      setInputValue(drawing.bedWidth / 10);
    }
  };

  return (
    <div className={classes.root}>
      {/* Machine Setup Section */}
      <Accordion 
        expanded={expandedPanel.machineSetup}
        onChange={handleAccordionChange('machineSetup')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="machine-setup-content"
          id="machine-setup-header"
        >
          <Typography className={classes.heading}>Sprøjte/Traktor opsætning</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-number-of-sprayers"
              type='number'
              value={drawing.sprayers}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, sprayers: Math.ceil(e.target.value) });
                }
              }}
              inputProps={{
                'aria-label': 'nr_sprayers',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Antal sprøjter</FormHelperText>
          </FormControl>
          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-sprayer-width"
              type='number'
              value={drawing.sprayerWidth/1000}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, sprayerWidth: e.target.value * 1000 });
                }
              }}
              endAdornment={<InputAdornment position="end">m</InputAdornment>}
              inputProps={{
                'aria-label': 'sprayerwidth',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Sprøjtebredde</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-tractor-width"
              type='number'
              value={drawing.tractorWidth/1000}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, tractorWidth: e.target.value * 1000 });
                }
              }}
              endAdornment={<InputAdornment position="end">m</InputAdornment>}
              inputProps={{
                'aria-label': 'tractor-width',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Tractor bredde</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-tire-width"
              type='number'
              value={drawing.tireWidth/10}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, tireWidth: e.target.value * 10 });
                }
              }}
              endAdornment={<InputAdornment position="end">cm</InputAdornment>}
              inputProps={{
                'aria-label': 'tire-width'            
              }}
            />
            <FormHelperText>Dæk bredde</FormHelperText>
          </FormControl>
        
        </AccordionDetails>
      </Accordion>

      {/* Bed Setup Section */}
      <Accordion 
        expanded={expandedPanel.bedSetup}
        onChange={handleAccordionChange('bedSetup')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="bed-setup-content"
          id="bed-setup-header"
        >
          <Typography className={classes.heading}>Bed Opsætning</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-bedwidth-width"
              type='number'
              value={inputValue}
              onChange={handleBedWidthChange}
              onBlur={handleBedWidthBlur}
              endAdornment={<InputAdornment position="end">cm</InputAdornment>}
              inputProps={{
                'aria-label': 'bedwidth',
                inputMode: 'numeric',
                min: '20'
              }}
              error={inputValue !== '' && parseInt(inputValue) < 20}
            />
            <FormHelperText 
              error={inputValue !== '' && parseInt(inputValue) < 20}
            >
              Bed bredde (min. 20cm)
              {inputValue !== '' && parseInt(inputValue) < 20 && " - Minimum 20cm required"}
            </FormHelperText>
          </FormControl>

         
        </AccordionDetails>
      </Accordion>

      {/* Sprayer Setup Section */}
      <Accordion 
        expanded={expandedPanel.sprayerSetup}
        onChange={handleAccordionChange('sprayerSetup')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="sprayer-setup-content"
          id="sprayer-setup-header"
        >
          <Typography className={classes.heading}>Lægge Opsætning</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
        <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-rowDistance"
              type='number'
              value={drawing.rowDistance/10}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, rowDistance: e.target.value * 10});
                }
              }}
              endAdornment={<InputAdornment position="end">cm</InputAdornment>}
              inputProps={{
                'aria-label': 'rowDistance'            
              }}
            />
            <FormHelperText>Rækkeafstand</FormHelperText>
          </FormControl>
          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-planter-rows"
              type='number'
              value={drawing.planterRows}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, planterRows: Math.ceil(e.target.value)});
                }
              }}
              inputProps={{
                'aria-label': 'planter rows'            
              }}
            />
            <FormHelperText>Lægger rækker</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-rows-per-bed"
              type='number'
              value={drawing.rowsPrBed}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, rowsPrBed: Math.ceil(e.target.value)});
                }
              }}
              inputProps={{
                'aria-label': 'rowsprbed'            
              }}
            />
            <FormHelperText>Rækker pr. bed</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-first-plant"
              type='number'
              value={drawing.firstRows}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, firstRows: Math.ceil(e.target.value)});
                }
              }}
              inputProps={{
                'aria-label': 'first-plant'            
              }}
            />
            <FormHelperText>Offset lægger x bede</FormHelperText>
          </FormControl>
          
        </AccordionDetails>
      </Accordion>

      {/* Plow Setup Section */}
      <Accordion 
        expanded={expandedPanel.plowSetup}
        onChange={handleAccordionChange('plowSetup')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="plow-setup-content"
          id="plow-setup-header"
        >
          <Typography className={classes.heading}>Plov Opsætning</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
        <FormControl>
            <FormControlLabel 
              control={
                <Switch
                  checked={drawing.offset}
                  onChange={(e) => {
                    setDrawing({ ...drawing, offset: e.target.checked});
                  }}
                />
              } 
              label="Offset bedplov" 
            />
          </FormControl>

          
          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-furrows"
              type='number'
              value={drawing.ploughFurrow}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, ploughFurrow: Math.ceil(e.target.value)});
                }
              }}
              inputProps={{
                'aria-label': 'furrows'            
              }}
            />
            <FormHelperText>Bedplov furer</FormHelperText>
          </FormControl>

          
        </AccordionDetails>
      </Accordion>

      {/* Irrigation Setup Section */}
      <Accordion 
        expanded={expandedPanel.irrigationSetup}
        onChange={handleAccordionChange('irrigationSetup')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="irrigation-setup-content"
          id="irrigation-setup-header"
        >
          <Typography className={classes.heading}>Vanding Opsætning</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-number-of-irrigation"
              type='number'
              value={drawing.irrigators}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, irrigators: Math.ceil(e.target.value) });
                }
              }}
              inputProps={{
                'aria-label': 'irrigators',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Antal vandingsmaskiner</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-irrigation-width"
              type='number'
              value={drawing.irrigationWidth/1000}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, irrigationWidth: e.target.value * 1000 });
                }
              }}
              endAdornment={<InputAdornment position="end">m</InputAdornment>}
              inputProps={{
                'aria-label': 'irrigation-width',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Vandingsbredde</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-irrigation-axle-width"
              type='number'
              value={drawing.irrigationAxleWidth/1000}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, irrigationAxleWidth: e.target.value * 1000 });
                }
              }}
              endAdornment={<InputAdornment position="end">m</InputAdornment>}
              inputProps={{
                'aria-label': 'irrigation-axle-width',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Vanding aksel bredde</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-irrigation-tire-width"
              type='number'
              value={drawing.irrigationTireWidth/10}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, irrigationTireWidth: e.target.value * 10 });
                }
              }}
              endAdornment={<InputAdornment position="end">cm</InputAdornment>}
              inputProps={{
                'aria-label': 'irrigation-tire-width'            
              }}
            />
            <FormHelperText>Vanding Dæk bredde</FormHelperText>
          </FormControl>
        </AccordionDetails>
      </Accordion>
      {/* System Setup Section */}
      <Accordion 
        expanded={expandedPanel.systemSetup}
        onChange={handleAccordionChange('systemSetup')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="system-setup-content"
          id="system-setup-header"
        >
          <Typography className={classes.heading}>System Opsætning</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-seperation-line-width"
              type='number'
              value={drawing.seperationLineWidth}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, seperationLineWidth: Math.ceil(e.target.value) });
                }
              }}
              inputProps={{
                'aria-label': 'seperationLineWidth',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Streg tykkelse</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-measueLineWidth-width"
              type='number'
              value={drawing.measureLineWidth}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, measureLineWidth: Math.ceil(e.target.value) });
                }
              }}
              inputProps={{
                'aria-label': 'measueLineWidth',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Måle linje tykkelse</FormHelperText>
          </FormControl>

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-numberSize-width"
              type='number'
              value={drawing.numberSize}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, numberSize: e.target.value });
                }
              }}           
              inputProps={{
                'aria-label': 'numberSize-width',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Tal størrelse</FormHelperText>
          </FormControl>    

          <FormControl variant="outlined">
            <OutlinedInput
              id="outlined-boomThickness-width"
              type='number'
              value={drawing.boomThickness}
              onChange={(e) => {
                if (e.target.value >= 0) {
                  setDrawing({ ...drawing, boomThickness: e.target.value });
                }
              }}           
              inputProps={{
                'aria-label': 'boomThickness-width',
                inputMode: 'numeric',
                pattern: '[0-9]'
              }}
            />
            <FormHelperText>Bom tykkelse</FormHelperText>
          </FormControl>          
        </AccordionDetails>
      </Accordion>

      {/* Upload and Download Drawing Buttons */}
      <div style={{ 
        padding: '20px', 
        borderTop: '1px solid #e0e0e0', 
        marginTop: '20px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
      }}>
        {/* Hidden file input */}
        <input
          type="file"
          accept=".json"
          id="upload-drawing"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              const reader = new FileReader();
              reader.onload = (event) => {
                try {
                  const uploadedDrawing = JSON.parse(event.target.result);
                  setDrawing(uploadedDrawing);
                  // Reset the file input
                  e.target.value = '';
                } catch (error) {
                  console.error('Error parsing JSON file:', error);
                  alert('Invalid JSON file. Please check the file format.');
                }
              };
              reader.readAsText(file);
            }
          }}
        />
        
        {/* Upload Button */}
        <Button 
          variant="outlined" 
          onClick={() => document.getElementById('upload-drawing').click()}
          sx={{ 
            borderColor: '#F22E17',
            color: '#F22E17',
            '&:hover': {
              borderColor: '#BE1F0D',
              backgroundColor: 'rgba(242, 46, 23, 0.04)'
            }
          }}
        >
          Gendan konfiguration
        </Button>

        {/* Download Button */}
        <Button 
          variant="contained" 
          onClick={() => {
            const dataStr = JSON.stringify(drawing, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'drawing-config.json';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }}
          sx={{ 
            backgroundColor: '#F22E17',
            '&:hover': {
              backgroundColor: '#BE1F0D'
            }
          }}
        >
          Gem konfiguration
        </Button>
      </div>
    </div>
  );
}